// Sprint 2 — Reorganización de configuración
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useToast } from "@/hooks/useToast";

export function DashboardQR() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [professional, setProfessional] = useState<{ slug: string; name: string } | null>(null);

  useEffect(() => {
    void (async () => {
      const r = await fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh);
      if (!r.ok) return;
      const d = (await r.json()) as { professional?: { slug?: string; name?: string } };
      if (!d.professional?.slug) return;
      setProfessional({
        slug: d.professional.slug,
        name: d.professional.name ?? "SFTurnos",
      });
    })();
  }, [refresh]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = professional?.slug ? `${origin}/book/${professional.slug}` : "";

  function printQr() {
    window.print();
  }

  async function downloadPng() {
    const svg = document.querySelector<SVGSVGElement>(".print-qr-core svg");
    if (!svg) return;
    const ser = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([ser], { type: "image/svg+xml;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    const qrImg = new Image();
    const logoImg = new Image();
    qrImg.crossOrigin = "anonymous";
    logoImg.crossOrigin = "anonymous";

    const loadImage = (img: HTMLImageElement, src: string) =>
      new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("No se pudo cargar imagen"));
        img.src = src;
      });

    try {
      await Promise.all([loadImage(qrImg, u), loadImage(logoImg, "/favicon-32x32.png")]);
      URL.revokeObjectURL(u);

      const w = 900;
      const h = 1220;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Fondo
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);

      // Contorno de recorte
      ctx.strokeStyle = "#94a3b8";
      ctx.lineWidth = 4;
      ctx.setLineDash([10, 10]);
      const cardX = 40;
      const cardY = 40;
      const cardW = w - 80;
      const cardH = h - 80;
      ctx.strokeRect(cardX, cardY, cardW, cardH);
      ctx.setLineDash([]);

      // Header (logo + marca)
      ctx.drawImage(logoImg, cardX + 180, cardY + 36, 56, 56);
      ctx.fillStyle = "#64748b";
      ctx.font = "24px sans-serif";
      ctx.fillText("SFTurnos", cardX + 252, cardY + 62);
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 40px sans-serif";
      ctx.fillText(professional?.name ?? "SFTurnos", cardX + 252, cardY + 96);

      // Título
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 56px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("¡Reservá tu turno!", w / 2, cardY + 180);

      // Caja QR
      const qrBoxW = 500;
      const qrBoxH = 500;
      const qrBoxX = (w - qrBoxW) / 2;
      const qrBoxY = cardY + 220;
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#e2e8f0";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.roundRect(qrBoxX, qrBoxY, qrBoxW, qrBoxH, 18);
      ctx.fill();
      ctx.stroke();

      ctx.drawImage(qrImg, qrBoxX + 50, qrBoxY + 50, qrBoxW - 100, qrBoxH - 100);

      // Logo centrado sobre el QR (igual que en vista/impresión)
      const qrInnerSize = qrBoxW - 100;
      const qrInnerX = qrBoxX + 50;
      const qrInnerY = qrBoxY + 50;
      const centerX = qrInnerX + qrInnerSize / 2;
      const centerY = qrInnerY + qrInnerSize / 2;
      const logoSize = 54;
      const logoPad = 14;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(centerX, centerY, (logoSize + logoPad) / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.drawImage(logoImg, centerX - logoSize / 2, centerY - logoSize / 2, logoSize, logoSize);

      // URL y ayuda
      ctx.fillStyle = "#64748b";
      ctx.font = "24px sans-serif";
      ctx.fillText(url, w / 2, qrBoxY + qrBoxH + 55);
      ctx.fillStyle = "#94a3b8";
      ctx.font = "26px sans-serif";
      ctx.fillText("Escaneá con la cámara para abrir la reserva", w / 2, qrBoxY + qrBoxH + 105);
      ctx.textAlign = "start";

      canvas.toBlob((b) => {
        if (!b) {
          toast.error("No se pudo generar la imagen.");
          return;
        }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `qr-reservas-${professional?.slug ?? "agenda"}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
        toast.success("Descarga lista");
      });
    } catch {
      URL.revokeObjectURL(u);
      toast.error("No se pudo exportar el PNG.");
    }
  }

  if (!professional) {
    return (
      <DashboardPageShell eyebrow="Configuración" title="Código QR">
        <LoadingFrame variant="light" minHeight={240} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Configuración"
      title="Código QR"
      description="Compartí un QR con el enlace directo a tu página de reservas."
    >
      <div
        className={cx(
          "print-qr-zone",
          cf.dashSurface,
          css({
            p: "8",
            maxW: "30rem",
            display: "flex",
            flexDir: "column",
            alignItems: "center",
            gap: "4",
          }),
        )}
      >
        <style>{`
          @media print {
            @page { size: auto; margin: 12mm; }
            body {
              background: #ffffff !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-hide { display: none !important; }
            .print-qr-zone {
              break-inside: avoid;
              page-break-inside: avoid;
              border: 2px dashed #94a3b8 !important; /* guía visual para recorte */
              border-radius: 14px !important;
              padding: 18px !important;
              box-shadow: none !important;
            }
          }
        `}</style>

        <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
          <img src="/favicon-32x32.png" alt="SFTurnos" className={css({ w: "8", h: "8", rounded: "sm" })} />
          <div>
            <p className={css({ fontSize: "xs", color: "#64748b", lineHeight: 1 })}>SFTurnos</p>
            <p className={css({ fontSize: "md", fontWeight: "bold", color: "#0f172a", lineHeight: 1.2 })}>{professional.name}</p>
          </div>
        </div>

        <p className={css({ fontSize: "xl", fontWeight: "semibold", color: "#0f172a", textAlign: "center" })}>
          ¡Reservá tu turno!
        </p>
        <div className={cx("print-qr-core", css({ bg: "white", p: "5", rounded: "xl", borderWidth: "1px", borderColor: "#e2e8f0" }))}>
          <QRCodeSVG
            value={url}
            size={240}
            level="H"
            includeMargin
            imageSettings={{
              src: "/favicon-32x32.png",
              x: undefined,
              y: undefined,
              height: 34,
              width: 34,
              excavate: true,
            }}
          />
        </div>
        <p className={css({ fontSize: "xs", color: "#64748b", textAlign: "center", wordBreak: "break-all" })}>{url}</p>
        <div className={css({ fontSize: "xs", color: "#94a3b8", textAlign: "center" })}>Escaneá con la cámara para abrir la reserva</div>
        <div className={cx("print-hide", css({ display: "flex", flexWrap: "wrap", gap: "3", justifyContent: "center" }))}>
          <button
            type="button"
            onClick={() => printQr()}
            className={css({
              px: "4",
              py: "2.5",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              border: "none",
              cursor: "pointer",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
            })}
          >
            Imprimir
          </button>
          <button
            type="button"
            onClick={() => void downloadPng()}
            className={css({
              px: "4",
              py: "2.5",
              rounded: "lg",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "#334155",
              borderWidth: "1px",
              borderColor: "#e2e8f0",
              bg: "#f8fafc",
              cursor: "pointer",
            })}
          >
            Descargar PNG
          </button>
        </div>
      </div>
    </DashboardPageShell>
  );
}
